import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, FileText, Users, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

const PATHWAY_STEPS = [
  {
    id: 'basic',
    title: 'Basic Classification',
    icon: AlertCircle,
    color: 'blue',
    description: 'Select the incident type to begin the classification pathway.',
    fields: ['Incident Type (Near Miss, First Aid, LTI, Fatality, Property Damage, etc.)'],
  },
  {
    id: 'severity',
    title: 'Severity Assessment',
    icon: AlertTriangle,
    color: 'orange',
    description: 'Determine the OSHA severity level based on the outcome of the incident.',
    fields: ['OSHA Severity Level', 'Days Away from Work (if LTI)'],
  },
  {
    id: 'injury',
    title: 'Injury Details',
    icon: FileText,
    color: 'red',
    description: 'Document specific injury information when the incident resulted in personal harm.',
    fields: ['Type of Injury', 'Body Part Affected', 'Medical Treatment Required', 'OSHA Recordable Status'],
    conditional: true,
    conditionNote: 'Required when severity involves personal injury',
  },
  {
    id: 'critical',
    title: 'Critical Incident Protocol',
    icon: AlertTriangle,
    color: 'red',
    description: 'Fatality incidents require immediate regulatory notification and senior management escalation.',
    fields: ['OSHA 8-hour notification', 'Senior management alert', 'Investigation team assignment'],
    conditional: true,
    conditionNote: 'Triggered only for Fatality classification',
    isCritical: true,
  },
  {
    id: 'investigation',
    title: 'Investigation Assignment',
    icon: Users,
    color: 'green',
    description: 'Assign an investigation lead and set the priority level for the investigation.',
    fields: ['Lead Investigator', 'Priority Level'],
  },
];

const colorMap = {
  blue: {
    header: 'bg-blue-50 border-blue-200',
    icon: 'bg-blue-600',
    badge: 'bg-blue-100 text-blue-700',
    title: 'text-blue-800',
    dot: 'bg-blue-500',
  },
  orange: {
    header: 'bg-orange-50 border-orange-200',
    icon: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-700',
    title: 'text-orange-800',
    dot: 'bg-orange-500',
  },
  red: {
    header: 'bg-red-50 border-red-200',
    icon: 'bg-red-600',
    badge: 'bg-red-100 text-red-700',
    title: 'text-red-800',
    dot: 'bg-red-500',
  },
  green: {
    header: 'bg-green-50 border-green-200',
    icon: 'bg-green-600',
    badge: 'bg-green-100 text-green-700',
    title: 'text-green-800',
    dot: 'bg-green-500',
  },
};

const ClassificationPathway = ({ formData, showDynamicFields }) => {
  const [openCards, setOpenCards] = useState({ basic: true, severity: false, injury: false, critical: false, investigation: false });

  const toggleCard = (id) => {
    setOpenCards(prev => ({ ...prev, [id]: !prev?.[id] }));
  };

  const getStepStatus = (stepId) => {
    switch (stepId) {
      case 'basic': return formData?.classification ? 'complete' : 'pending';
      case 'severity': return formData?.severity ? 'complete' : 'pending';
      case 'injury': return (formData?.injuryType && formData?.bodyPart && formData?.medicalTreatment) ? 'complete' : 'pending';
      case 'critical': return 'critical';
      case 'investigation': return formData?.assignedInvestigator ? 'complete' : 'pending';
      default: return 'pending';
    }
  };

  const isVisible = (step) => {
    if (step?.id === 'injury') return showDynamicFields?.injuryDetails;
    if (step?.id === 'critical') return showDynamicFields?.fatalityDetails;
    return true;
  };

  const visibleSteps = PATHWAY_STEPS?.filter(isVisible);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="flex items-center gap-3 p-4 border-b border-gray-200">
        <AlertCircle className="w-5 h-5 text-blue-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Classification Pathway</h2>
          <p className="text-xs text-gray-500">Step-by-step guide — expand each card to review requirements</p>
        </div>
        <span className="ml-auto text-xs text-gray-400">
          {visibleSteps?.filter(s => getStepStatus(s?.id) === 'complete')?.length}/{visibleSteps?.length} complete
        </span>
      </div>

      <div className="p-4 space-y-3">
        {visibleSteps?.map((step, idx) => {
          const StepIcon = step?.icon;
          const status = getStepStatus(step?.id);
          const colors = colorMap?.[step?.isCritical ? 'red' : step?.color];
          const isOpen = openCards?.[step?.id];

          return (
            <div
              key={step?.id}
              className={`rounded-lg border-2 overflow-hidden transition-all ${
                step?.isCritical ? 'border-red-300' : status === 'complete' ? 'border-green-300' : 'border-gray-200'
              }`}
            >
              {/* Card Header — always visible, click to toggle */}
              <button
                type="button"
                onClick={() => toggleCard(step?.id)}
                className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${
                  step?.isCritical ? 'bg-red-50 hover:bg-red-100' : status === 'complete' ? 'bg-green-50 hover:bg-green-100' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  status === 'complete' ? 'bg-green-600' : step?.isCritical ? 'bg-red-600' : colors?.icon
                }`}>
                  {status === 'complete'
                    ? <CheckCircle2 className="w-4 h-4 text-white" />
                    : <StepIcon className="w-4 h-4 text-white" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400">Step {idx + 1}</span>
                    {step?.conditional && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Conditional</span>
                    )}
                    {status === 'complete' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Complete</span>
                    )}
                    {step?.isCritical && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">⚠ Critical</span>
                    )}
                  </div>
                  <p className={`text-sm font-semibold mt-0.5 ${step?.isCritical ? 'text-red-800' : status === 'complete' ? 'text-green-800' : 'text-gray-800'}`}>
                    {step?.title}
                  </p>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              </button>

              {/* Card Body — collapsible */}
              {isOpen && (
                <div className="px-4 pb-4 pt-3 border-t border-gray-200 bg-white">
                  <p className="text-sm text-gray-600 mb-3">{step?.description}</p>
                  {step?.conditionNote && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
                      ℹ {step?.conditionNote}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {step?.fields?.map((field, i) => (
                      <span key={i} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                        step?.isCritical ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-100 text-gray-700 border-gray-200'
                      }`}>
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClassificationPathway;